import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Stack, Typography, Grid, TextField, MenuItem, Button } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

import { fleetApi } from '../../../api/axiosClient';
import { useVehicleStatusDashboardDynamic } from '../../../stores/vehicleStatusStore';
// import { usePolygonTypeList } from '../../../stores/polygonTypeStore';
import { fetchRetase, fetchRetaseDetail, dashboardCache } from '../../../stores/retaseStore';
// import { getLicenseStatusData } from '../../../stores/licenseStore';
import { useDriverLicenseDetailDashboardList } from '../../../stores/driverLicenseDetailStore';

import useVehicleNotifications from '../../../hooks/useVehicleNotifications';
import { useLanguage } from '../../../utils/i18n';

import SummaryAsset from './SummaryAsset';
import LicenseReminder from './LicenseReminder';
import TopDriverByRitase from './TopDriverByRetase';
import StatusOperational from './StatusOperational';
import { timezone } from '../../../utils/timeZone';

const selectFieldStyle = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#485D9A' },
    '&:hover fieldset': { borderColor: '#485D9A' },
    '&.Mui-focused fieldset': { borderColor: '#485D9A' }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#485D9A' }
};

const datePickerStyle = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#485D9A' },
    '&:hover fieldset': { borderColor: '#485D9A' },
    '&.Mui-focused fieldset': { borderColor: '#485D9A' }
  }
};

const DashboardAsset = () => {
  const getTzParam = () => {
    const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
    return `?tz=${tz}`;
  };
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [allVehicles, setAllVehicles] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [operationalData, setOperationalData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    totalVehicle: 0,
    totalVehicleEngineOn: 0,
    totalVehicleMaintenance: 0,
    totalActiveDriver: 0,
    totalRitase: 0,
    totalRitaseDrivers: 0,
    masterVehicles: [],
    gpsUpdateVehicles: [],
    activeVehicles: [],
    maintenanceVehicles: [],
    activeDrivers: [],
    ritaseTrips: []
  });

  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0, text: '' });
  const [autoRefresh, setAutoRefresh] = useState(0);
  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState(dayjs().format('YYYY-MM-DDT00:00:00'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DDTHH:mm:59'));

  const pollingInterval = useMemo(() => {
    if (periodType === 'today' && autoRefresh > 0) {
      return autoRefresh * 60000;
    }
    return 100000;
  }, [periodType, autoRefresh]);

  const { data: statusData } = useVehicleStatusDashboardDynamic(pollingInterval);
  const { data: licenseData, refetch: refetchLicense } = useDriverLicenseDetailDashboardList(100);
  // Hook for vehicle notifications
  useVehicleNotifications(statusData);

  const calculateAssetStatus = useCallback((statusDataParam, allVehiclesParam, allDriversParam) => {
    let engineOn = 0;
    let maintenance = 0;
    let activeDriverCount = 0;
    const activeVehiclesArr = [];
    const maintenanceVehiclesArr = [];

    if (Array.isArray(statusDataParam)) {
      statusDataParam.forEach((v) => {
        const statusRaw = String(v.status || v.position || v.location || '').toLowerCase();
        const isEngineOn = v.engine === 'ON' || v.engine === true || statusRaw.includes('engine on') || statusRaw.includes('moving');
        const isMaintenance = statusRaw.includes('at maintenance') || statusRaw.includes('maintenance');

        if (isEngineOn) {
          engineOn++;
          activeVehiclesArr.push(v);
        }
        if (isMaintenance) {
          maintenance++;
          maintenanceVehiclesArr.push(v);
        }
      });
    }

    const activeDriverArr = (allDriversParam || []).filter((d) => d.status === true || d.is_active === true);
    activeDriverCount = activeDriverArr.length;

    setSummaryData((prev) => ({
      ...prev,
      totalVehicle: Array.isArray(allVehiclesParam) ? allVehiclesParam.length : 0,
      totalVehicleEngineOn: engineOn,
      totalVehicleMaintenance: maintenance,
      totalActiveDriver: activeDriverCount,
      masterVehicles: Array.isArray(allVehiclesParam) ? allVehiclesParam : [],
      gpsUpdateVehicles: Array.isArray(statusDataParam) ? statusDataParam : [],
      activeVehicles: activeVehiclesArr,
      maintenanceVehicles: maintenanceVehiclesArr,
      activeDrivers: activeDriverArr
    }));
  }, []);

  const fetchRitaseData = useCallback(
    async (allVehiclesParam, signal) => {
      if (!Array.isArray(allVehiclesParam) || allVehiclesParam.length === 0) return;

      const startIso = dayjs(fromDate).format('YYYY-MM-DDTHH:mm:ss');
      const endIso = dayjs(toDate).format('YYYY-MM-DDTHH:mm:ss');
      const companyId = localStorage.getItem('companyId') || '';

      const cacheKey = periodType === 'today' ? `asset_${companyId}_today` : `asset_${companyId}_${startIso}_${endIso}`;
      const cachedData = dashboardCache.get(cacheKey);
      if (cachedData) {
        setOperationalData(cachedData.operationalData);
        setSummaryData((prev) => ({
          ...prev,
          ...cachedData.summaryData
        }));
        return;
      }

      try {
        const totalVehicles = allVehiclesParam.length;
        let completedVehicles = 0;
        setFetchProgress({
          current: 0,
          total: totalVehicles,
          text: t('dashboardAsset.fetchingRitaseInit', `Preparing to fetch ritase for ${totalVehicles} vehicles...`)
        });

        const promises = allVehiclesParam.map(async (v) => {
          const vId = v.id || v.vehicle_id;
          if (!vId) {
            completedVehicles++;
            return null;
          }
          const vCompanyId = v.company_id ?? v.company ?? companyId;
          try {
            let dates = [];
            let curr = dayjs(startIso).startOf('day');
            const end = dayjs(endIso).startOf('day');
            while (curr.isBefore(end) || curr.isSame(end, 'day')) {
              dates.push(curr);
              curr = curr.add(1, 'day');
            }

            const dailyPromises = dates.map(async (dateObj) => {
              let dStartIso = dateObj.startOf('day');
              let dEndIso = dateObj.endOf('day');

              if (dateObj.isSame(dayjs(startIso), 'day')) dStartIso = dayjs(startIso);
              if (dateObj.isSame(dayjs(endIso), 'day')) dEndIso = dayjs(endIso);

              const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
              const finalStartIso = dStartIso.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
              const finalEndIso = dEndIso.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

              try {
                const res = await fetchRetase({
                  vehicleId: String(vId),
                  companyId: String(vCompanyId),
                  startTime: finalStartIso,
                  endTime: finalEndIso,
                  tz,
                  signal
                });
                const apiDataRaw = res?.data.retase || [];

                const uniqueTripsMap = new Map();
                apiDataRaw.forEach((tripData) => {
                  const routes = tripData.routes || [];
                  const details = tripData.details || [];

                  const spatialNames = routes.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
                  const routeTitle = spatialNames.length > 0 ? spatialNames.join(' - ') : `Route ID: ${tripData.route_trip_id}`;

                  let earliestStart = null;
                  let latestEnd = null;
                  details.forEach((d) => {
                    const st = dayjs(d.start);
                    const en = dayjs(d.end);
                    if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
                    if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
                  });

                  const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
                  const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';

                  const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
                  if (!uniqueTripsMap.has(uniqueKey)) {
                    uniqueTripsMap.set(uniqueKey, tripData);
                  }
                });
                const apiData = Array.from(uniqueTripsMap.values());

                const processedTrips = await Promise.all(
                  apiData.map(async (tripData) => {
                    const routes = tripData.routes || [];
                    const details = tripData.details || [];

                    if (details.length === 0) {
                      return {
                        vehicle_id: vId,
                        license_plate: v.license_plate || v.name || String(vId),
                        route_trip_id: tripData.route_trip_id,
                        driver_id: tripData.driver_id,
                        driver_name: tripData.driver_name,
                        in_time: tripData.start_time || tripData.in_time || 'no-start',
                        out_time: tripData.end_time || tripData.out_time || 'no-end',
                        duration: 0,
                        routes: routes,
                        details: [],
                        ritase_count: tripData.total_trips || 1
                      };
                    }

                    const detailPromises = details.map(async (d, dIdx) => {
                      const st = dayjs(d.start);
                      const en = dayjs(d.end);
                      const finalStartIso = st.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
                      const finalEndIso = en.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

                      let nextRouteTimeIso = undefined;
                      if (details.length > 1 && dIdx < details.length - 1) {
                        const nextSt = dayjs(details[dIdx + 1].start);
                        nextRouteTimeIso = nextSt.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
                      }

                      const finalStart = st.format('YYYY-MM-DD HH:mm:ss');
                      const finalEnd = en.format('YYYY-MM-DD HH:mm:ss');

                      let actualStartTime = finalStart;
                      let actualEndTime = finalEnd;

                      try {
                        const detailRes = await fetchRetaseDetail({
                          companyId: String(vCompanyId),
                          vehicleId: String(vId),
                          routeTripId: String(tripData.route_trip_id),
                          startTime: finalStartIso,
                          endTime: finalEndIso,
                          nextRouteTime: nextRouteTimeIso,
                          tz,
                          page: 0,
                          rowsPerPage: 10000,
                          signal
                        });
                        const deepDetailLogs = detailRes?.data?.data || detailRes?.data || [];
                        if (deepDetailLogs.length > 0) {
                          actualStartTime = dayjs(deepDetailLogs[0].start_time).format('YYYY-MM-DD HH:mm:ss');
                          actualEndTime = dayjs(deepDetailLogs[deepDetailLogs.length - 1].end_time).format('YYYY-MM-DD HH:mm:ss');
                        }
                      } catch (err) {
                        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                          console.error(err);
                        }
                      }

                      return {
                        vehicle_id: vId,
                        license_plate: v.license_plate || v.name || String(vId),
                        route_trip_id: tripData.route_trip_id,
                        driver_id: tripData.driver_id,
                        driver_name: tripData.driver_name,
                        in_time: actualStartTime,
                        out_time: actualEndTime,
                        duration: d.dur || 0,
                        routes: routes,
                        details: [d]
                      };
                    });

                    const resolvedDetails = await Promise.all(detailPromises);

                    return {
                      vehicle_id: vId,
                      license_plate: v.license_plate || v.name || String(vId),
                      route_trip_id: tripData.route_trip_id,
                      driver_id: tripData.driver_id,
                      driver_name: tripData.driver_name,
                      in_time: resolvedDetails[0]?.in_time || tripData.start_time || tripData.in_time,
                      out_time: resolvedDetails[resolvedDetails.length - 1]?.out_time || tripData.end_time || tripData.out_time,
                      duration: 0,
                      routes: routes,
                      details: resolvedDetails,
                      ritase_count: resolvedDetails.length || tripData.total_trips || 1
                    };
                  })
                );
                return processedTrips;
              } catch {
                return [];
              }
            });

            const dailyResults = await Promise.all(dailyPromises);
            const combinedTrips = dailyResults.flat();

            const vehicleTotalRitase = combinedTrips.reduce((acc, curr) => acc + (curr ? curr.ritase_count || 0 : 0), 0);

            return { vId, trips: combinedTrips, vehicleTotalRitase };
          } catch (err) {
            if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
              console.error(err);
            }
            return null;
          } finally {
            completedVehicles++;
            const pct = Math.round((completedVehicles / totalVehicles) * 100);
            setFetchProgress({
              current: completedVehicles,
              total: totalVehicles,
              text: t('dashboardAsset.fetchingRitaseProgress', `Searching data from ${v.license_plate || v.name || vId}... (${pct}%)`)
            });
          }
        });

        const results = await Promise.all(promises);

        let totalRitaseDetails = 0;
        let allRawTrips = [];
        const uniqueDrivers = new Set();

        results.forEach((res) => {
          if (!res) return;
          totalRitaseDetails += res.vehicleTotalRitase;
          allRawTrips = allRawTrips.concat(res.trips);

          res.trips.forEach((t) => {
            if (t.driver_id) {
              uniqueDrivers.add(t.driver_id);
            }
          });
        });

        setOperationalData(allRawTrips);

        const sumData = {
          totalRitase: totalRitaseDetails,
          totalRitaseDrivers: uniqueDrivers.size,
          ritaseTrips: allRawTrips
        };

        setSummaryData((prev) => ({
          ...prev,
          ...sumData
        }));

        dashboardCache.set(cacheKey, { operationalData: allRawTrips, summaryData: sumData });
      } catch (err) {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          console.error('Failed to fetch aggregated ritase data', err);
        }
      } finally {
        setFetchProgress({ current: 0, total: 0, text: '' });
      }
    },
    [fromDate, toDate, periodType, t]
  );

  const abortControllerRef = useRef(null);

  const executeDataGeneration = useCallback(
    async (signal) => {
      setLoading(true);

      try {
        // 1. Fetch Companies
        const compRes = await fleetApi.post(
          `/company/search${getTzParam()}`,
          {
            paging: { start: 0, length: 1000 },
            orders: { columns: ['name'], ascending: true }
          },
          { signal }
        );
        const companies = compRes?.data?.data || compRes?.data || [];
        const companyIds = companies.map((c) => c.id).join(',');

        // 2. Fetch All Vehicles
        let fetchedVehicles = [];
        let vStart = 0;
        let limit = 100;
        let hasMoreVehicles = true;

        while (hasMoreVehicles) {
          const payload = {
            paging: { start: vStart, length: limit },
            orders: { columns: ['license_plate'], ascending: true },
            joins: [{ name: 'company', column_results: ['name'] }]
          };
          if (companyIds) {
            payload.filters = [{ column: 'company_id', operator: 'IN', value: companyIds }];
          }

          const resp = await fleetApi.post(`/vehicle/search${getTzParam()}`, payload, { signal });
          const data = resp?.data?.data ?? resp?.data ?? [];
          const totalRecord = resp?.data?.totalRecord ?? resp?.data?.recordsTotal ?? 0;

          if (Array.isArray(data)) fetchedVehicles = fetchedVehicles.concat(data);

          vStart += limit;
          if (vStart >= totalRecord || data.length === 0) {
            hasMoreVehicles = false;
          }
        }
        setAllVehicles(fetchedVehicles);

        // 3. Fetch All Drivers
        let fetchedDrivers = [];
        let dStart = 0;
        let hasMoreDrivers = true;

        while (hasMoreDrivers) {
          const payload = {
            paging: { start: dStart, length: limit },
            orders: { columns: ['name'], ascending: true },
            joins: [{ name: 'company', column_results: ['name'] }]
          };
          if (companyIds) {
            payload.filters = [{ column: 'company_id', operator: 'IN', value: companyIds }];
          }

          const resp = await fleetApi.post(`/driver/search${getTzParam()}`, payload, { signal });
          const data = resp?.data?.data ?? resp?.data ?? [];
          const totalRecord = resp?.data?.totalRecord ?? resp?.data?.recordsTotal ?? 0;

          if (Array.isArray(data)) fetchedDrivers = fetchedDrivers.concat(data);

          dStart += limit;
          if (dStart >= totalRecord || data.length === 0) {
            hasMoreDrivers = false;
          }
        }
        setAllDrivers(fetchedDrivers);

        calculateAssetStatus(statusData, fetchedVehicles, fetchedDrivers);
        await fetchRitaseData(fetchedVehicles, signal);
        refetchLicense();
      } catch (error) {
        if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
          console.error('Failed to fetch serverside data', error);
        }
      }

      setLoading(false);
    },
    [calculateAssetStatus, fetchRitaseData, refetchLicense, statusData]
  );

  const handleGenerateData = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    executeDataGeneration(abortControllerRef.current.signal);
  }, [executeDataGeneration]);

  // Handle statusData updates without refetching everything
  useEffect(() => {
    if (!loading) {
      calculateAssetStatus(statusData, allVehicles, allDrivers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData]);

  useEffect(() => {
    handleGenerateData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (periodType !== 'today' || autoRefresh === 0) return;
    const intervalId = setInterval(() => {
      const todayStr = dayjs().format('YYYY-MM-DDTHH:mm:59');
      const startStr = dayjs().format('YYYY-MM-DDT00:00:00');
      setFromDate(startStr);
      setToDate(todayStr);
      handleGenerateData();
    }, autoRefresh * 60000);
    return () => clearInterval(intervalId);
  }, [autoRefresh, periodType, handleGenerateData]);

  const handlePeriodChange = (e) => {
    const val = e.target.value;
    setPeriodType(val);
    if (val !== 'today') {
      setAutoRefresh(0);
    }
    const todayStr = dayjs().format('YYYY-MM-DDTHH:mm:59');

    if (val === 'today') {
      setFromDate(dayjs().format('YYYY-MM-DDT00:00:00'));
      setToDate(todayStr);
    } else if (val === 'last_week') {
      setFromDate(dayjs().subtract(7, 'day').format('YYYY-MM-DDT00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DDT23:59:59'));
    } else if (val === 'last_month') {
      setFromDate(dayjs().subtract(1, 'month').format('YYYY-MM-DDT00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DDT23:59:59'));
    } else {
      // default custom
      setFromDate(dayjs().format('YYYY-MM-DDT00:00:00'));
      setToDate(dayjs().format('YYYY-MM-DDT23:59:59'));
    }
  };

  // --- Helper conditional rendering widgets ---
  const appSettingStr = localStorage.getItem('appSetting');
  const appSetting = appSettingStr ? JSON.parse(appSettingStr) : {};
  const widgetsConfig = appSetting?.widgets || null;
  const showWidget = (key) => {
    if (!widgetsConfig) return true;
    if (!widgetsConfig.asset) return true;
    return widgetsConfig.asset[key] !== false;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {/* {(loading || isStatusLoading || isLicenseLoading) && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">{t('dashboardFuel.fetchingAssetData', 'Fetching Asset data...')}</Typography>
          </Box>
        )} */}

        <Box sx={{ mb: 1, p: 2, bgcolor: '#ffffff', borderRadius: 2, boxShadow: 1 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Refresh Every & AutoRefresh Toggle */}
            <Grid item size={{ xs: 12, md: 3 }} display="flex" alignItems="center" gap={1}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('dashboardFuel.refreshEvery', 'Refresh Every')}
                value={autoRefresh}
                onChange={(e) => setAutoRefresh(Number(e.target.value))}
                sx={selectFieldStyle}
                disabled={loading || periodType !== 'today'}
              >
                <MenuItem value={0}>{t('dashboardFuel.off', 'Off')}</MenuItem>
                <MenuItem value={10}>{t('dashboardFuel.minutes10', '10 Minutes')}</MenuItem>
                <MenuItem value={30}>{t('dashboardFuel.minutes30', '30 Minutes')}</MenuItem>
                <MenuItem value={60}>{t('dashboardFuel.minutes60', '60 Minutes')}</MenuItem>
              </TextField>
              <Button
                variant="outlined"
                onClick={handleGenerateData}
                disabled={loading}
                sx={{
                  width: 35,
                  height: 35,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 0,
                  borderColor: '#485D9A',
                  '&:hover': { borderColor: '#485D9A' }
                }}
              >
                <HistoryIcon sx={{ color: '#485D9A', fontWeight: 700 }} />
              </Button>
            </Grid>

            {/* Periode Selection */}
            <Grid item size={{ xs: 12, md: 2 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('dashboardFuel.period', 'Period')}
                value={periodType}
                onChange={handlePeriodChange}
                sx={selectFieldStyle}
                disabled={loading}
              >
                <MenuItem value="today">{t('dashboardFuel.today', 'Today')}</MenuItem>
                <MenuItem value="last_week">{t('time.lastWeek', 'Last week')}</MenuItem>
                <MenuItem value="last_month">{t('time.lastMonth', 'Last month')}</MenuItem>
                <MenuItem value="custom">{t('time.custom', 'Custom')}</MenuItem>
              </TextField>
            </Grid>

            {/* Custom Date Pickers */}
            {periodType === 'custom' && (
              <Grid item size={{ xs: 12, md: 4 }}>
                <Stack direction="row" spacing={1}>
                  <DateTimePicker
                    label={t('dashboardFuel.fromDate', 'From Date')}
                    format="DD/MM/YYYY HH:mm"
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    value={fromDate ? dayjs(fromDate) : null}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    onChange={(newValue) => setFromDate(newValue ? newValue.format('YYYY-MM-DDTHH:mm:ss') : '')}
                    disabled={loading}
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: datePickerStyle } }}
                  />
                  <DateTimePicker
                    label={t('dashboardFuel.toDate', 'To Date')}
                    format="DD/MM/YYYY HH:mm"
                    ampm={false}
                    timeSteps={{ minutes: 1 }}
                    value={toDate ? dayjs(toDate) : null}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    onChange={(newValue) => setToDate(newValue ? newValue.format('YYYY-MM-DDTHH:mm:ss') : '')}
                    disabled={loading}
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: datePickerStyle } }}
                  />
                </Stack>
              </Grid>
            )}

            {/* Generate Button & Info */}
            <Grid item xs={12} md="auto" flex={1} display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={handleGenerateData}
                disabled={loading}
                sx={{ px: 3, fontWeight: 'bold', height: 40, whiteSpace: 'nowrap' }}
              >
                {loading ? t('dashboardFuel.generating', 'Generating...') : t('dashboardFuel.generate', 'Generate')}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Info Period Alert Box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            bgcolor: '#E3F2FD',
            borderRadius: 2,
            border: '1px solid #90CAF9',
            mb: 2
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#1976d2', mt: 0.2 }} />
          <Box>
            <Typography variant="body2" color="#0D47A1" fontWeight={600} mb={0.5}>
              {t('dashboardAsset.periodInfoTitle', 'Historical Data Scope')}
            </Typography>
            <Typography variant="body2" color="#0D47A1" sx={{ lineHeight: 1.5 }}>
              {t(
                'dashboardAsset.periodInfo',
                'The selected period only applies to historical data (Total Ritase & Top Driver). Vehicle status remains real-time.'
              )}
              {fromDate && toDate && (
                <span style={{ display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                  {t('dashboardFuel.showingDataFrom', 'Showing data from')} <strong>{dayjs(fromDate).format('DD MMM, HH:mm')}</strong>{' '}
                  {t('dashboardFuel.to', 'to')} <strong>{dayjs(toDate).format('DD MMM, HH:mm')}</strong>.
                </span>
              )}
            </Typography>
          </Box>
        </Box>

        {/* Fetch Progress Bar */}
        {loading && fetchProgress.total > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#ffffff', borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="body2" color="#485D9A" mb={1} fontWeight="bold">
              {fetchProgress.text}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(fetchProgress.current / fetchProgress.total) * 100}
              sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' } }}
            />
          </Box>
        )}

        <Stack spacing={1}>
          {showWidget('summary-asset') && <SummaryAsset data={summaryData} loading={loading} periodType={periodType} />}
          {showWidget('license-driver-reminder') && <LicenseReminder data={licenseData} loading={loading} />}
          {showWidget('status-operational') && (
            <StatusOperational rawTrips={operationalData} vehicleStatus={statusData} loading={loading} />
          )}
          {showWidget('top-driver-by-ritase') && <TopDriverByRitase rawTrips={operationalData} loading={loading} />}
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardAsset;
